"use client";

import type {
  QuickPayOutput,
  QuickPayReceiptContext,
} from "@tillix/api-contract/client-schemas";
import { useT } from "@tillix/i18n/react";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import { Input } from "@tillix/ui-atoms/input";
import { SegmentedControl } from "@tillix/ui-atoms/segmented-control";
import { observer } from "mobx-react-lite";
import { Controller } from "react-hook-form";
import { FEE_BY_METHOD } from "@/features/pay/state/payment-fees";
import { AccountNumberInput } from "@/features/payment-method/components/AccountNumberInput";
import { RoutingNumberInput } from "@/features/payment-method/components/RoutingNumberInput";
import { useQuickPayCheckForm } from "./hooks/use-quick-pay-check-form";
import QuickPayFormLayout from "./QuickPayFormLayout";

interface QuickPayCheckFormProps {
  tenantId: string;
  externalId: string;
  nameOnAccount?: string;
  zipCode?: string;
  invoiceNumber?: string;
  amount: number;
  onSuccess: (result: QuickPayOutput, context: QuickPayReceiptContext) => void;
  onCancel: () => void;
}

function QuickPayCheckForm({
  tenantId,
  externalId,
  nameOnAccount,
  zipCode,
  invoiceNumber,
  amount,
  onSuccess,
  onCancel,
}: QuickPayCheckFormProps) {
  const { t } = useT();
  const { t: tInputs } = useT("inputs");
  const {
    form,
    handleFormSubmit,
    rootError,
    paymentStatus,
    isSubmitting,
    isValid,
  } = useQuickPayCheckForm({
    tenantId,
    externalId,
    nameOnAccount,
    zipCode,
    invoiceNumber,
    amount,
    onSuccess,
  });

  return (
    <QuickPayFormLayout
      amount={amount}
      fee={FEE_BY_METHOD.BANK_ACCOUNT}
      feeLabel={t("pay.achProcessingFee")}
      form={form}
      isSubmitting={isSubmitting}
      isValid={isValid}
      keyPrefix="qp-check"
      onCancel={onCancel}
      onSubmit={handleFormSubmit}
      paymentStatus={paymentStatus}
      rootError={rootError}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Controller
          control={form.control}
          name="firstName"
          render={({ field, fieldState }) => (
            <FieldRoot name={field.name}>
              <FieldLabel htmlFor="qp-first-name">
                {t("quickPay.firstName")}
              </FieldLabel>
              <FieldControl
                render={(defaultProps) => (
                  <Input
                    {...defaultProps}
                    {...field}
                    autoComplete="given-name"
                    id="qp-first-name"
                    placeholder={t("quickPay.firstName")}
                  />
                )}
              />
              <FieldError>
                {fieldState.error
                  ? tInputs("validation.invalidName")
                  : undefined}
              </FieldError>
            </FieldRoot>
          )}
        />
        <Controller
          control={form.control}
          name="lastName"
          render={({ field, fieldState }) => (
            <FieldRoot name={field.name}>
              <FieldLabel htmlFor="qp-last-name">
                {t("quickPay.lastName")}
              </FieldLabel>
              <FieldControl
                render={(defaultProps) => (
                  <Input
                    {...defaultProps}
                    {...field}
                    autoComplete="family-name"
                    id="qp-last-name"
                    placeholder={t("quickPay.lastName")}
                  />
                )}
              />
              <FieldError>
                {fieldState.error
                  ? tInputs("validation.invalidName")
                  : undefined}
              </FieldError>
            </FieldRoot>
          )}
        />
      </div>

      <RoutingNumberInput id="qp-routing" />
      <AccountNumberInput id="qp-account" name="checkAccountNumber" />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Controller
          control={form.control}
          name="fundingAccountType"
          render={({ field, fieldState }) => (
            <FieldRoot name={field.name}>
              <FieldLabel id="qp-funding-type-label">
                {t("quickPay.accountType")}
              </FieldLabel>
              <SegmentedControl
                ariaLabelledBy="qp-funding-type-label"
                disabled={isSubmitting}
                id="qp-funding-type"
                onChange={field.onChange}
                options={[
                  { label: t("quickPay.checkingType"), value: "Checking" },
                  { label: t("quickPay.savingsType"), value: "Savings" },
                ]}
                value={field.value}
              />
              <FieldError>
                {fieldState.error ? tInputs("validation.required") : undefined}
              </FieldError>
            </FieldRoot>
          )}
        />
        <Controller
          control={form.control}
          name="checkAccountType"
          render={({ field, fieldState }) => (
            <FieldRoot name={field.name}>
              <FieldLabel id="qp-entity-type-label">
                {t("quickPay.entityType")}
              </FieldLabel>
              <SegmentedControl
                ariaLabelledBy="qp-entity-type-label"
                disabled={isSubmitting}
                id="qp-entity-type"
                onChange={field.onChange}
                options={[
                  { label: t("quickPay.personalType"), value: "Personal" },
                  { label: t("quickPay.businessType"), value: "Business" },
                ]}
                value={field.value}
              />
              <FieldError>
                {fieldState.error ? tInputs("validation.required") : undefined}
              </FieldError>
            </FieldRoot>
          )}
        />
      </div>

      <Controller
        control={form.control}
        name="address1"
        render={({ field, fieldState }) => (
          <FieldRoot name={field.name}>
            <FieldLabel htmlFor="qp-address">
              {t("quickPay.streetAddress")}
            </FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  {...field}
                  autoComplete="street-address"
                  id="qp-address"
                  placeholder={t("quickPay.streetAddress")}
                />
              )}
            />
            <FieldError>
              {fieldState.error ? tInputs("validation.required") : undefined}
            </FieldError>
          </FieldRoot>
        )}
      />

      <Controller
        control={form.control}
        name="email"
        render={({ field, fieldState }) => (
          <FieldRoot name={field.name}>
            <FieldLabel htmlFor="qp-check-email">
              {t("quickPay.emailForReceipt")}
            </FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <Input
                  {...defaultProps}
                  {...field}
                  autoComplete="email"
                  id="qp-check-email"
                  placeholder="your@email.com"
                  type="email"
                />
              )}
            />
            <FieldError>
              {fieldState.error
                ? tInputs("validation.invalidEmail")
                : undefined}
            </FieldError>
          </FieldRoot>
        )}
      />
    </QuickPayFormLayout>
  );
}

export default observer(QuickPayCheckForm);
